// «Generación con IA» de un ítem del maestro: cómo se produce al generar el
// informe, qué sugiere el sistema mientras nadie lo confirma y cómo se rotula
// la insignia "IA · …" de las filas. Puro: ni red ni React.
//
// Los textos (etiquetas y notas) son los del diseño "Mantenedor de Informes".
// El modo guardado es `null` mientras nadie lo confirme: la pantalla muestra
// entonces la SUGERENCIA derivada del tipo y el nombre, con trazo punteado, y
// el generador exigirá un modo confirmado por una persona.

import { PESTANAS, type SeccionInforme } from "./informes";
import type { TipoDeContenido } from "./plantillas";

export type ModoDeProduccion =
  | "sistema"
  | "tabla"
  | "calculo"
  | "texto_fijo"
  | "texto_validado"
  | "redactado_ia"
  | "imagen_anexo";

/** Los siete modos en el orden del diseño (el mismo del enum del servidor). */
export const MODOS_DE_PRODUCCION: { valor: ModoDeProduccion; etiqueta: string }[] = [
  { valor: "sistema", etiqueta: "Sistema" },
  { valor: "tabla", etiqueta: "Tabla" },
  { valor: "calculo", etiqueta: "Cálculo" },
  { valor: "texto_fijo", etiqueta: "Texto fijo" },
  { valor: "texto_validado", etiqueta: "Texto ya validado" },
  { valor: "redactado_ia", etiqueta: "Redactado por IA" },
  { valor: "imagen_anexo", etiqueta: "Imagen / anexo" },
];

/**
 * Qué pieza del expediente muestra un ítem al armar el documento: el
 * vocabulario CERRADO `RecursoDelDocumento` del servidor (`maestro_items.recurso`),
 * en su mismo orden. Un test fija la lista; si el servidor agrega un recurso, este
 * es el lugar donde se suma.
 */
export type RecursoDelDocumento =
  | "ficha_identificacion"
  | "tabla_roles"
  | "tabla_legal_roles"
  | "tabla_suelos"
  | "tabla_aguas"
  | "tabla_plantaciones"
  | "tabla_construcciones"
  | "tabla_instalaciones"
  | "tabla_maquinaria"
  | "tabla_comparables"
  | "tabla_homologacion"
  | "cuadro_valor_suelos"
  | "cuadro_valor_aguas"
  | "cuadro_valor_plantaciones"
  | "cuadro_valor_construcciones"
  | "cuadro_valor_instalaciones"
  | "cuadro_valor_maquinaria"
  | "cuadro_resumen_valorizacion"
  | "imagen_satelital"
  | "imagen_limites_generales"
  | "imagen_vias_acceso"
  | "imagen_vista_alejada"
  | "imagen_mosaico_suelos"
  | "imagen_plano_regulador"
  | "imagen_deslindes"
  | "fotografias"
  | "tabla_legal_roles"
  | "imagen_plano_roles_sii"
  | "imagen_area_estudio"
  | "imagen_plano_subdivision"
  | "imagen_mapa_referencias"
  | "fotografias_inmueble"
  | "fotografias_dron"
  | "fotografias_construcciones"
  | "fotografias_instalaciones"
  | "fotografias_maquinaria"
  | "fotografias_plantaciones"
  | "firma_tasador"
  | "firma_revisor";

export const RECURSOS_DEL_DOCUMENTO: { valor: RecursoDelDocumento; etiqueta: string }[] = [
  { valor: "ficha_identificacion", etiqueta: "Ficha de identificación" },
  { valor: "tabla_roles", etiqueta: "Tabla de roles" },
  { valor: "tabla_legal_roles", etiqueta: "Tabla legal de roles (superficies, avalúo e inscripción)" },
  { valor: "tabla_suelos", etiqueta: "Tabla de clases de suelo" },
  { valor: "tabla_aguas", etiqueta: "Tabla de aguas (fuentes y obras)" },
  { valor: "tabla_plantaciones", etiqueta: "Tabla de plantaciones" },
  { valor: "tabla_construcciones", etiqueta: "Tabla de construcciones" },
  { valor: "tabla_instalaciones", etiqueta: "Tabla de instalaciones y obras" },
  { valor: "tabla_maquinaria", etiqueta: "Tabla de maquinarias y equipos" },
  { valor: "tabla_comparables", etiqueta: "Tabla de comparables de mercado" },
  { valor: "tabla_homologacion", etiqueta: "Tabla de homologación" },
  { valor: "cuadro_valor_suelos", etiqueta: "Cuadro de valor: suelos" },
  { valor: "cuadro_valor_aguas", etiqueta: "Cuadro de valor: derechos de agua" },
  { valor: "cuadro_valor_plantaciones", etiqueta: "Cuadro de valor: plantaciones" },
  { valor: "cuadro_valor_construcciones", etiqueta: "Cuadro de valor: construcciones" },
  { valor: "cuadro_valor_instalaciones", etiqueta: "Cuadro de valor: instalaciones y obras" },
  { valor: "cuadro_valor_maquinaria", etiqueta: "Cuadro de valor: maquinarias y equipos" },
  { valor: "cuadro_resumen_valorizacion", etiqueta: "Resumen de la valorización" },
  { valor: "imagen_satelital", etiqueta: "Imagen satelital del predio" },
  { valor: "imagen_limites_generales", etiqueta: "Encuadre: límites generales" },
  { valor: "imagen_vias_acceso", etiqueta: "Encuadre: vías de acceso y localidades" },
  { valor: "imagen_vista_alejada", etiqueta: "Encuadre: vista alejada y ciudades cercanas" },
  { valor: "imagen_mosaico_suelos", etiqueta: "Mosaico de clases de suelo" },
  { valor: "imagen_plano_regulador", etiqueta: "Plano regulador" },
  { valor: "imagen_deslindes", etiqueta: "Capturas de los deslindes de cada rol" },
  { valor: "fotografias", etiqueta: "Fotografías de terreno" },
  { valor: "imagen_plano_roles_sii", etiqueta: "Anexo: plano de roles del SII" },
  { valor: "imagen_area_estudio", etiqueta: "Anexo: área de estudio" },
  { valor: "imagen_plano_subdivision", etiqueta: "Anexo: plano de subdivisión" },
  { valor: "imagen_mapa_referencias", etiqueta: "Anexo: mapa de las referencias de mercado" },
  { valor: "fotografias_inmueble", etiqueta: "Anexo: fotografías del inmueble" },
  { valor: "fotografias_dron", etiqueta: "Anexo: vistas con dron" },
  { valor: "fotografias_construcciones", etiqueta: "Anexo: fotografías de las construcciones" },
  { valor: "fotografias_instalaciones", etiqueta: "Anexo: fotografías de las instalaciones y obras" },
  { valor: "fotografias_maquinaria", etiqueta: "Anexo: fotografías de las maquinarias y equipos" },
  { valor: "fotografias_plantaciones", etiqueta: "Anexo: fotografías de las plantaciones" },
  { valor: "firma_tasador", etiqueta: "Firma del tasador" },
  { valor: "firma_revisor", etiqueta: "Firma del revisor" },
];

export function etiquetaDelRecurso(recurso: RecursoDelDocumento): string {
  return RECURSOS_DEL_DOCUMENTO.find((r) => r.valor === recurso)?.etiqueta ?? recurso;
}

/** Los modos en que el servidor guarda el recurso; en los demás lo descarta. */
const MODOS_CON_RECURSO: ModoDeProduccion[] = ["sistema", "tabla", "calculo", "imagen_anexo"];

export function admiteRecurso(modo: ModoDeProduccion): boolean {
  return MODOS_CON_RECURSO.includes(modo);
}

/** Qué hace el generador con un ítem de cada modo; se lee bajo el selector. */
export const NOTA_DEL_MODO: Record<ModoDeProduccion, string> = {
  sistema:
    "Lo completa el sistema con los datos del informe (portada, folio, fechas). No se revisa.",
  tabla: "Se arma con una tabla del expediente. Se corrige en el expediente.",
  calculo: "Muestra las cifras fijadas en la fase de valor.",
  texto_fijo: "Texto estándar de la plantilla. Solo lectura al generar.",
  texto_validado: "Usa el texto que el tasador escribió y validó en su pestaña.",
  redactado_ia: "La IA lo propone y el tasador lo valida al generar el informe.",
  imagen_anexo: "Usa las fotos, mapas o documentos elegidos.",
};

export function etiquetaDelModo(modo: ModoDeProduccion): string {
  return MODOS_DE_PRODUCCION.find((m) => m.valor === modo)?.etiqueta ?? modo;
}

/**
 * Las pestañas del expediente de las que puede salir un «texto ya validado»,
 * con el rótulo con que se ven en la pantalla del informe.
 */
export const SECCIONES_DE_ORIGEN: { valor: SeccionInforme; etiqueta: string }[] = PESTANAS.map(
  (p) => ({ valor: p.seccion, etiqueta: p.etiqueta }),
);

export function etiquetaDeLaSeccion(seccion: string): string {
  return SECCIONES_DE_ORIGEN.find((s) => s.valor === seccion)?.etiqueta ?? seccion;
}

/** Nombres que se redactan con IA, salvo que parezcan una definición o una metodología. */
const NO_SE_REDACTA = /^definici|^metodolog/;
const SE_REDACTA = /resumen|conclusi|descrip|acceso|ubicaci|entorno|mercado|análisis|observaci/;

/**
 * El modo que el sistema sugiere mientras nadie confirma uno, deducido del tipo
 * de contenido y del nombre. Es la misma deducción del diseño: tabla → Tabla,
 * cuadro de valor → Cálculo, imágenes → Imagen / anexo, y un texto es
 * "Redactado por IA" si su nombre habla de describir, resumir o concluir.
 * Lo demás (firmas, documentos anexos) lo completa el sistema.
 */
export function modoSugerido(tipo: TipoDeContenido, nombre: string): ModoDeProduccion {
  const n = nombre.toLowerCase();

  if (tipo === "tabla") return "tabla";
  if (tipo === "cuadro_de_valor") return "calculo";
  if (tipo === "imagen" || tipo === "imagen_mapa") return "imagen_anexo";
  if (tipo === "texto") {
    return !NO_SE_REDACTA.test(n) && SE_REDACTA.test(n) ? "redactado_ia" : "texto_fijo";
  }
  return "sistema";
}

/** El modo que se muestra: el confirmado, o si no hay, la sugerencia (`sugerido`). */
export function modoEfectivo(
  guardado: ModoDeProduccion | null,
  tipo: TipoDeContenido,
  nombre: string,
): { modo: ModoDeProduccion; sugerido: boolean } {
  return guardado
    ? { modo: guardado, sugerido: false }
    : { modo: modoSugerido(tipo, nombre), sugerido: true };
}

export interface InsigniaDeIa {
  /** "IA · final", "IA · 3" o "IA · ?". */
  texto: string;
  /** Verde si ya se indicó de qué depende; ámbar si falta. */
  completa: boolean;
  /** Trazo punteado: el modo es una sugerencia y nadie lo confirmó. */
  sugerida: boolean;
  titulo: string;
}

/**
 * La insignia de las filas: solo para los ítems redactados por IA. «final» si
 * se redacta al final (resume el resto), el número de dependencias si las
 * tiene, y «?» si falta indicar de qué depende.
 */
export function insigniaDeIa({
  modo,
  sugerido,
  dependencias,
  alFinal,
}: {
  modo: ModoDeProduccion;
  sugerido: boolean;
  dependencias: number;
  alFinal: boolean;
}): InsigniaDeIa | null {
  if (modo !== "redactado_ia") return null;

  const detalle = alFinal
    ? " · se redacta al final"
    : dependencias > 0
      ? ` · ${dependencias} ${dependencias === 1 ? "dependencia" : "dependencias"}`
      : " · falta indicar de qué depende";

  return {
    texto: `IA · ${alFinal ? "final" : dependencias > 0 ? dependencias : "?"}`,
    completa: alFinal || dependencias > 0,
    sugerida: sugerido,
    titulo:
      `Redactado por IA${detalle}` +
      (sugerido ? " · sugerido: deducido del tipo y el nombre, confírmelo en el detalle" : ""),
  };
}
